import styled, { keyframes, css } from 'styled-components'
import media from 'styles/media'
import spacing from 'styles/spacing'
import { Fonts } from 'components/base-ui'
import { THEMES_UI } from 'brands'

const { screenMdMin } = media

const FadeIn = keyframes`
  0% { opacity: 0; }
  100% { opacity: 1; }
`

const slideStyle = css`
  margin-right: 20px;
  margin-bottom: 0;
  display: inline-block;
  width: 135px;
  padding-left: 0;
  padding-right: 0;
  padding-bottom: 20px;
  vertical-align: top;

  &:first-child {
    margin-left: 20px;
  }
`

const Card = styled.div<{ mode: 'normal' | 'slide' }>`
  margin-bottom: 5px;
  width: 50%;
  padding-top: 15px;
  padding-left: 10px;
  padding-right: 10px;
  scroll-snap-align: start;

  @media (${screenMdMin}) {
    scroll-snap-align: inherit;
  }

  ${({ mode }) => mode === 'slide' && slideStyle}
`

interface CardThumbnailWrapperProps {
  recommended?: boolean
  exclusive?: boolean
  memberItem?: boolean
  mode?: 'normal' | 'slide'
}

const CardThumbnailWrapper = styled.div<CardThumbnailWrapperProps>`
  position: relative;
  margin-bottom: ${spacing.md}px;
  width: 100%;
  border-radius: 10px;

  ${({ recommended, theme }) =>
    recommended &&
    css`
      background: ${theme.colors.tertiary};
    `}

  ${({ exclusive, theme }) =>
    exclusive &&
    css`
      background: ${theme.colors.success};
    `}

    ${({ memberItem, theme }) =>
    memberItem &&
    css`
      background: ${theme.colors.primary};
    `}

  &::before {
    content: '';
    display: block;
    padding-top: 100%;
  }

  ${({ mode }) =>
    mode === 'slide' &&
    css`
      height: 180px;

      &::before {
        display: none;
      }
    `}
`

const CardThumbnail = styled.div<{
  exclusive?: boolean
  recommended?: boolean
  image1x: string
  image2x: string
  image3x: string
}>`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  border-radius: 10px;
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  animation: ${FadeIn} 0.6s forwards;

  background-image: -webkit-image-set(
    url(${({ image1x }) => `'${image1x}'`}) 1x,
    url(${({ image2x }) => `'${image2x}'`}) 2x,
    url(${({ image3x }) => `'${image3x}'`}) 3x
  );
  background-image: image-set(
    url(${({ image1x }) => `'${image1x}'`}) 1x,
    url(${({ image2x }) => `'${image2x}'`}) 2x,
    url(${({ image3x }) => `'${image3x}'`}) 3x
  );

  ${({ exclusive, recommended }) =>
    (exclusive || recommended) &&
    css`
      border-radius: 8px;
      top: 3px;
      right: 3px;
      bottom: 3px;
      left: 3px;
    `}
`

const CardName = styled(Fonts.BaseTitleBold)`
  color: ${(props) => props.theme.colors.base};
  white-space: initial;
  padding-right: ${spacing.sm}px;
  margin-bottom: ${spacing.xs}px;
  font-size: 13px;
  line-height: 20px;
`

const CardPriceSection = styled.div`
  display: flex;
  justify-content: space-between;
`

const CardTags = styled.div`
  margin-bottom: 2px;
  font-size: 10px;
  color: ${(props) => props.theme.colors.gray300};
  text-overflow: ellipsis;
  overflow: hidden;
`

const CardPriceBeforeDiscount = styled.span`
  margin-right: 5px;
  color: ${(props) => props.theme.colors.baseGrey};
  text-decoration: line-through;
`

const CardPrice = styled(Fonts.BaseTitleBold)<{ margin?: boolean }>`
  color: ${(props) => props.theme.colors.price};

  ${({ margin }) =>
    margin &&
    css`
      margin-left: 5px;
    `}
`

interface BtnAddToCartPlusProps {
  isOutline?: boolean
  hide?: boolean
}

const BtnAddToCartPlus = styled.div<BtnAddToCartPlusProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  min-width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.primary};
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.1), 0 6px 20px rgba(0, 0, 0, 0.05);
  transition: 0.6s;

  i {
    font-size: 14px;
    color: ${({ theme }) => theme.colors.white};
    transition: 0.6s;
  }

  &:active {
    transition: 0s;
    background-color: ${({ theme }) => theme.colors.white};
    border: none;

    i {
      transition: 0s;
      color: ${({ theme }) => theme.colors.primary};
    }
  }

  ${({ isOutline, theme }) =>
    isOutline &&
    css`
      background-color: ${theme.colors.white};
      border: 1px solid ${theme.colors.primary};

      i {
        color: ${theme.colors.primary};
      }

      &:active {
        background-color: ${theme.colors.primary};
        border: 1px solid ${theme.colors.primary};

        i {
          color: ${theme.colors.white};
        }
      }
    `}

  ${({ hide }) =>
    hide &&
    css`
      visibility: hidden;
    `}
`

const PlaceholderImage = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-size: cover;
  background-position: center;
`

const ProductLabelInventoryWrapper = styled.div<{ themeUI: THEMES_UI; isEligibleMember: boolean }>`
  position: absolute;
  left: 10px;
  bottom: ${({ themeUI, isEligibleMember }) => (themeUI === THEMES_UI.GON && isEligibleMember ? '40px' : '13px')};
`

const ProductLabelWrapper = styled.div`
  position: absolute;
  bottom: 13px;
  right: -4px;
`

const LabelExcludedPromotion = styled(Fonts.SmallTitleRegular)`
  margin-top: 5px;
  font-size: 10px;
  color: ${(props) => props.theme.colors.infoDark};
`

const BadgePromotionWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
`

export {
  Card,
  CardThumbnailWrapper,
  CardThumbnail,
  CardName,
  CardPriceSection,
  CardTags,
  CardPriceBeforeDiscount,
  CardPrice,
  BtnAddToCartPlus,
  PlaceholderImage,
  ProductLabelInventoryWrapper,
  ProductLabelWrapper,
  LabelExcludedPromotion,
  BadgePromotionWrapper,
}
